import { useMemo, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { get, find } from "lodash";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { useDateSettings } from "src/contexts/global/ApolloSettingsContext";
import {
  useFiltersForQuery,
  useFteMode,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { getTeamInsights } from "src/pages/TeamDetailPage/tabs/TeamInsightsTab/queries.graphql";
import {
  calculateLayersMetric,
  calculateSpanMetric,
  calculateManagerToICRatioMetric,
  calculateManagementOverheadMetric,
  calculateHeadcountReductionMetric,
  calculateTeamDepthFromTop,
} from "src/util/teamMetrics";
import { ENTITY_TYPES } from "src/consts/entityTypes";

/**
 * Custom hook to fetch and calculate team metrics for org chart
 * Can be used in Org Charts, Team Details pages, and other components that need team metrics
 * Note: Currently requires OrgChartContext. Can be extended to support other contexts in the future.
 * @param {string} scenario - Scenario name: 'conservative', 'standard', or 'aggressive' (default: 'standard')
 * @returns {object} - { metrics, orgMetrics, loading, hasTeam, currentTeam }
 */
export const useTeamMetrics = (scenario = "standard") => {
  const { state } = useOrgChartContext();
  const filter = useFiltersForQuery();
  const fteMode = useFteMode();
  const dateSettings = useDateSettings();

  // Track the last selected team ID separately - only update when a team is selected, not a person
  // This ensures metrics stay on the last team when a person is clicked
  const lastTeamIdRef = useRef(null);

  // Check if the selected entity is a team or person
  const selectedEntityId = state?.expandedPanel?.data?.selectedEntity;
  const selectedEntityType = state?.expandedPanel?.data?.selectedEntityType;
  const isTeamSelected =
    !selectedEntityType || selectedEntityType === ENTITY_TYPES.GROUP;

  // Only update team ID when a team is selected - keep last team when a person is selected
  // Also initialize from currentViewTeam if ref is empty (handles initial load)
  const currentTeamId = useMemo(() => {
    if (isTeamSelected && selectedEntityId) {
      // Team selected - update the tracked team ID
      lastTeamIdRef.current = selectedEntityId;
      return selectedEntityId;
    }
    // Person selected or no selection - use last tracked team, fall back to currentViewTeam
    // Initialize ref if it's empty and currentViewTeam is available (for initial load)
    const fallbackTeamId = lastTeamIdRef.current || state?.currentViewTeam?.id;
    if (!lastTeamIdRef.current && state?.currentViewTeam?.id) {
      lastTeamIdRef.current = state.currentViewTeam.id;
    }
    return fallbackTeamId;
  }, [isTeamSelected, selectedEntityId, state?.currentViewTeam?.id]);

  // Get the actual team object - use the tracked team ID
  const currentTeam = useMemo(() => {
    if (currentTeamId && state?.teams?.[currentTeamId]) {
      return state.teams[currentTeamId];
    }
    return state?.currentViewTeam;
  }, [currentTeamId, state?.teams, state?.currentViewTeam]);

  // Calculate team's depth from top of organization
  const teamDepthFromTop = useMemo(() => {
    return calculateTeamDepthFromTop(currentTeam);
  }, [currentTeam]);

  // Build filter WITHOUT team to get total org layers
  const orgFilter = useMemo(() => {
    // Remove team filter to get org-wide data, but preserve other filters
    const { teams, ...otherFilters } = filter;
    return otherFilters;
  }, [filter]);

  // Variables for fetching total org layers (no team filter)
  const orgVariables = useMemo(() => {
    const { fromDate, toDate, comparisonMode } = dateSettings;
    const dateTime = toDate ? toDate.utc().valueOf() : null;
    const comparisonDateTime =
      comparisonMode && fromDate ? fromDate.utc().valueOf() : null;

    return {
      filter: orgFilter,
      dateTime,
      comparisonDateTime,
      fteMode,
    };
  }, [dateSettings, orgFilter, fteMode]);

  // Fetch total org layers (without team filter)
  const { data: orgInsightsData, loading: orgInsightsLoading } = useQuery(
    getTeamInsights,
    {
      variables: orgVariables,
      fetchPolicy: "cache-and-network",
      skip: !currentTeamId, // Only fetch when we have a team selected
    }
  );

  // Build filter with current team - ensures insights are calculated for the active team only
  // The filter restricts the GraphQL query to only return data for the selected team
  // This matches the Insights page behavior: when a team is selected, layers/spans are calculated
  // for people in that team (and optionally sub-teams if includeSubTeams is true)
  const teamFilter = useMemo(() => {
    if (!currentTeamId) {
      return filter;
    }
    // Preserve any existing person-level filters (location, role, etc.) but override team filter
    // to focus on the selected team. This ensures layers/spans calculations work the same way
    // as on the Insights page when filtering by team.
    return {
      ...filter,
      teams: {
        teams: [currentTeamId],
        // Respect the global filter setting for includeSubTeams to match Insights page behavior
        // When false: calculates layers/spans for people directly in this team only
        // When true: calculates layers/spans for people in this team and all descendant teams
        includeSubTeams: get(filter, "teams.includeSubTeams", false),
      },
    };
  }, [filter, currentTeamId]);

  const variables = useMemo(() => {
    const { fromDate, toDate, comparisonMode } = dateSettings;
    const dateTime = toDate ? toDate.utc().valueOf() : null;
    const comparisonDateTime =
      comparisonMode && fromDate ? fromDate.utc().valueOf() : null;

    return {
      filter: teamFilter,
      dateTime,
      comparisonDateTime,
      fteMode,
    };
  }, [dateSettings, teamFilter, fteMode]);

  const {
    data: insightsData,
    loading: insightsLoading,
    refetch,
  } = useQuery(getTeamInsights, {
    variables,
    fetchPolicy: "cache-and-network", // Use cache-and-network like TeamInsightsTab
    skip: !currentTeamId,
    notifyOnNetworkStatusChange: true, // Ensure loading state updates
    // Force Apollo to refetch when variables change by including currentTeamId in the query key
    // This ensures the query is treated as new when team changes
  });

  // Force refetch when currentTeamId changes to ensure fresh data
  // This is critical - Apollo may not detect deep changes in the filter object
  // So we explicitly refetch when the team ID changes
  // Note: currentTeamId only updates when a team is selected, not when a person is selected
  useEffect(() => {
    if (!currentTeamId || !refetch) {
      return undefined;
    }

    // Build fresh variables to ensure we have the latest teamFilter
    // This ensures we're refetching with the correct team ID
    const { fromDate, toDate, comparisonMode } = dateSettings;
    const dateTime = toDate ? toDate.utc().valueOf() : null;
    const comparisonDateTime =
      comparisonMode && fromDate ? fromDate.utc().valueOf() : null;

    const freshVariables = {
      filter: teamFilter,
      dateTime,
      comparisonDateTime,
      fteMode,
    };

    // Refetch with fresh variables - this ensures metrics update when team changes
    // Apollo should also automatically refetch when variables changes, but this ensures it happens
    // Use a small delay to ensure React state has fully updated
    const timeoutId = setTimeout(() => {
      refetch(freshVariables)
        .then(() => {
          // Refetch completed - Apollo should have updated insightsData
        })
        .catch(() => {
          // Silently handle errors - Apollo will retry if needed
        });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentTeamId, refetch, teamFilter, dateSettings, fteMode]);

  // Extract insights and ensure it updates when data changes
  // Memoize based on insightsData to ensure fresh data triggers recalculation
  // insightsData updates when currentTeamId changes (via refetch), so no need to include currentTeamId
  const insights = useMemo(() => {
    return get(insightsData, "insights", {});
  }, [insightsData]);

  // Calculate metrics - ensure layers metric recalculates when insights change
  // Extract singleStats to ensure proper dependency tracking for layers calculation
  const singleStats = useMemo(
    () => get(insights, "singleStats", []),
    [insights]
  );

  // Extract org-wide metrics from org-wide query
  const orgInsights = useMemo(() => {
    return get(orgInsightsData, "insights", {});
  }, [orgInsightsData]);

  const orgSingleStats = useMemo(() => {
    return get(orgInsights, "singleStats", []);
  }, [orgInsights]);

  // Extract total org layers
  const totalOrgLayers = useMemo(() => {
    const orgLayersStat = find(orgSingleStats, { key: "layers" });
    return orgLayersStat?.max || null;
  }, [orgSingleStats]);

  // Calculate org-wide metrics for comparison
  const orgSpanMetric = useMemo(() => {
    return calculateSpanMetric(orgInsights, scenario);
  }, [orgInsights, scenario]);

  const orgManagerToICRatioMetric = useMemo(() => {
    return calculateManagerToICRatioMetric(orgInsights, scenario);
  }, [orgInsights, scenario]);

  const orgManagementOverheadMetric = useMemo(() => {
    return calculateManagementOverheadMetric(orgInsights, scenario);
  }, [orgInsights, scenario]);

  const layersMetric = useMemo(() => {
    // Ensure we're using the latest singleStats array for layers calculation
    const insightsWithStats = { ...insights, singleStats };
    return calculateLayersMetric(insightsWithStats, scenario, {
      teamDepthFromTop,
      totalOrgLayers,
    });
  }, [insights, singleStats, scenario, teamDepthFromTop, totalOrgLayers]);
  const spanMetric = useMemo(
    () => calculateSpanMetric(insights, scenario),
    [insights, scenario]
  );
  const managerToICRatioMetric = useMemo(
    () => calculateManagerToICRatioMetric(insights, scenario),
    [insights, scenario]
  );
  const managementOverheadMetric = useMemo(
    () => calculateManagementOverheadMetric(insights, scenario),
    [insights, scenario]
  );

  // Get includeSubTeams from filter - ensure it updates when team changes
  const includeSubTeams = useMemo(
    () => get(filter, "teams.includeSubTeams", false),
    [filter]
  );
  const headcountReductionMetric = useMemo(
    () =>
      calculateHeadcountReductionMetric(insights, includeSubTeams, scenario),
    [insights, includeSubTeams, scenario]
  );

  // Extract total org member count (without duplicates)
  const totalOrgMemberCount = useMemo(() => {
    return get(orgInsights, "total", null);
  }, [orgInsights]);

  return {
    metrics: {
      layers: layersMetric,
      span: spanMetric,
      managerToICRatio: managerToICRatioMetric,
      managementOverhead: managementOverheadMetric,
      headcountReduction: headcountReductionMetric,
    },
    orgMetrics: {
      span: orgSpanMetric,
      managerToICRatio: orgManagerToICRatioMetric,
      managementOverhead: orgManagementOverheadMetric,
      memberCount: totalOrgMemberCount,
    },
    loading: insightsLoading || orgInsightsLoading,
    hasTeam: !!currentTeamId,
    currentTeam, // Expose currentTeam so MetricsPanel can display team name/badge
  };
};
